import { Plus, Search, User } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNotification } from '../contexts/NotificationContext'
import { createOrGetPlayer, searchPlayers as searchPlayersApi } from '../services/playerService'
import { slotGender } from '../lib/playerGender'

export default function PlayerTypeahead({
  value,
  onChange,
  placeholder = "Enter player name",
  divisionGender = null, // Division gender: 'male', 'female', or 'mixed'
  divisionTeamSize = 1, // 1 for singles, 2 for doubles
  playerPosition = null, // For mixed doubles: 1 (female) or 2 (male)
  excludePlayerIds = [], // Array of player IDs to exclude from search results
  error = false, // Error state for validation
  validationKey = null,
  tabIndex, // Tab index for keyboard navigation
  onFocusNext = null, // Optional callback to control where focus goes after player selection
  sportId = null, // Sport to auto-assign when creating a new player from bracket context
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const [results, setResults] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const blurTimeoutRef = useRef(null)
  const [creating, setCreating] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [hasInvalidInput, setHasInvalidInput] = useState(false)
  const wrapperRef = useRef(null)
  const inputRef = useRef(null)
  const notify = useNotification()

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Search players as user types
  useEffect(() => {
    const searchPlayers = async () => {
      if (searchTerm.length < 1) {
        setResults([])
        return
      }

      setLoading(true)
      try {
        const data = await searchPlayersApi(searchTerm, { p_sport_id: sportId || null })

        // Apply gender filtering based on division configuration
        let filteredResults = data || []

        const desiredGender = slotGender(divisionGender, divisionTeamSize, playerPosition)

        if (desiredGender) {
          filteredResults = filteredResults.filter(player => player.gender === desiredGender)
        }
        
        // Filter out players that are already used in the bracket
        if (excludePlayerIds.length > 0) {
          filteredResults = filteredResults.filter(player => !excludePlayerIds.includes(player.id))
        }
        
        // Limit to 5 results to keep "Create Player" option visible
        filteredResults = filteredResults.slice(0, 5)

        setResults(filteredResults)
      } catch (error) {
        console.error('Error searching players:', error)
        setResults([])
      } finally {
        setLoading(false)
      }
    }

    const debounceTimer = setTimeout(searchPlayers, 300)
    return () => clearTimeout(debounceTimer)
  }, [searchTerm, divisionGender, divisionTeamSize, playerPosition, sportId, excludePlayerIds])

  // Handle player selection
  const handleSelectPlayer = (player) => {
    // Cancel any pending blur timer to prevent it from stealing focus
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
    onChange({
      id: player.id,
      name: player.name,
      gender: player.gender
    })
    setSearchTerm('')
    setIsOpen(false)
    setSelectedIndex(-1)
    setHasInvalidInput(false)

    // Move focus to next field after React finishes re-rendering.
    // Use setTimeout to let React commit the state update, then rAF to ensure
    // the DOM is painted before we try to find/focus the next element.
    setTimeout(() => {
      requestAnimationFrame(() => {
        const focusBefore = document.activeElement

        // Use parent-provided focus callback if available
        if (onFocusNext) {
          onFocusNext()
          // Verify focus actually moved — if it didn't, use DOM-walking fallback
          // to prevent the browser from defaulting focus to the top of the page
          if (document.activeElement !== focusBefore && document.activeElement !== document.body) {
            return
          }
        }

        // Fallback: walk the DOM to find the next focusable element after this wrapper
        if (!wrapperRef.current) return

        const allFocusable = Array.from(document.querySelectorAll(
          'input:not([tabindex="-1"]):not([disabled]), button:not([tabindex="-1"]):not([disabled]), select:not([tabindex="-1"]):not([disabled]), textarea:not([tabindex="-1"]):not([disabled])'
        )).filter(el => {
          const rect = el.getBoundingClientRect()
          return rect.width > 0 && rect.height > 0 && el.offsetParent !== null
        })

        const nextElement = allFocusable.find(el => {
          if (wrapperRef.current.contains(el)) return false
          const comparison = wrapperRef.current.compareDocumentPosition(el)
          return comparison & Node.DOCUMENT_POSITION_FOLLOWING
        })

        if (nextElement) {
          nextElement.focus()
        }
      })
    }, 100)
  }

  // Handle TBD selection
  const handleSelectTBD = () => {
    // Cancel any pending blur timer to prevent it from stealing focus
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
    onChange({
      id: 'TBD',
      name: 'TBD',
      gender: null
    })
    setSearchTerm('')
    setIsOpen(false)
    setSelectedIndex(-1)
    setHasInvalidInput(false)

    // Move focus to next field after React finishes re-rendering.
    // Use setTimeout to let React commit the state update, then rAF to ensure
    // the DOM is painted before we try to find/focus the next element.
    setTimeout(() => {
      requestAnimationFrame(() => {
        const focusBefore = document.activeElement

        // Use parent-provided focus callback if available
        if (onFocusNext) {
          onFocusNext()
          // Verify focus actually moved — if it didn't, use DOM-walking fallback
          // to prevent the browser from defaulting focus to the top of the page
          if (document.activeElement !== focusBefore && document.activeElement !== document.body) {
            return
          }
        }

        // Fallback: walk the DOM to find the next focusable element after this wrapper
        if (!wrapperRef.current) return

        const allFocusable = Array.from(document.querySelectorAll(
          'input:not([tabindex="-1"]):not([disabled]), button:not([tabindex="-1"]):not([disabled]), select:not([tabindex="-1"]):not([disabled]), textarea:not([tabindex="-1"]):not([disabled])'
        )).filter(el => {
          const rect = el.getBoundingClientRect()
          return rect.width > 0 && rect.height > 0 && el.offsetParent !== null
        })

        const nextElement = allFocusable.find(el => {
          if (wrapperRef.current.contains(el)) return false
          const comparison = wrapperRef.current.compareDocumentPosition(el)
          return comparison & Node.DOCUMENT_POSITION_FOLLOWING
        })

        if (nextElement) {
          nextElement.focus()
        }
      })
    }, 100)
  }

  // Gender a player created from this slot is saved with (shown on the
  // Create option so the admin sees it before clicking).
  const newPlayerGender = slotGender(divisionGender, divisionTeamSize, playerPosition)

  // Create new player
  const handleCreatePlayer = async () => {
    if (!searchTerm.trim()) return

    // Cancel any pending blur timer to prevent it from closing the dropdown
    // or setting hasInvalidInput during the async RPC call
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)

    // Prevent creating players with reserved name "TBD"
    if (searchTerm.trim().toUpperCase() === 'TBD') {
      await notify.alert({
        title: 'Invalid Player Name',
        description: 'Cannot create a player with the name "TBD". This is a reserved name.',
        variant: 'warning'
      })
      return
    }

    // Keep the input focused during creation to prevent focus from escaping
    inputRef.current?.focus()

    setCreating(true)
    try {
      const data = await createOrGetPlayer({
        player_name: searchTerm.trim(),
        player_gender: newPlayerGender,
        p_sport_id: sportId || null,
      })

      // create_or_get_player returns an existing row when the name is taken.
      // The search above hides other-gender rows, so "Create" could silently
      // hand back e.g. a female BYE in a men's slot (ticket 953d9afd). Say so
      // instead of letting the admin believe a new player was made.
      if (newPlayerGender && data?.gender && data.gender !== newPlayerGender) {
        notify.warning(
          `A player named "${data.name}" already exists as ${data.gender}, so no new ${newPlayerGender} player was created. Change their gender in Player Management if it is wrong.`
        )
      }

      // Select the newly created player (includes focus-to-next logic)
      handleSelectPlayer(data)
    } catch (error) {
      console.error('Error creating player:', error)
      notify.error('Failed to create player: ' + error.message)
    } finally {
      setCreating(false)
    }
  }

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    const hasTBD = !searchTerm.trim()

    if (!isOpen) {
      if (e.key === 'ArrowDown') {
        setIsOpen(true)
      }
      return
    }

    // Calculate total options: optional TBD + results + create option (if search term exists)
    const totalOptions = (hasTBD ? 1 : 0) + results.length + (searchTerm.trim() ? 1 : 0)

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => 
          prev < totalOptions - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => prev > -1 ? prev - 1 : -1)
        break
      case 'Enter':
        e.preventDefault()
        const baseIndex = hasTBD ? 1 : 0

        if (hasTBD && selectedIndex === 0) {
          // TBD option
          handleSelectTBD()
        } else if (hasTBD && selectedIndex === -1 && totalOptions === 1) {
          // No explicit selection with arrows and TBD is the only option
          handleSelectTBD()
        } else if (
          selectedIndex >= baseIndex &&
          selectedIndex < baseIndex + results.length
        ) {
          // Player from results (navigated with arrows)
          handleSelectPlayer(results[selectedIndex - baseIndex])
        } else if (
          searchTerm.trim() &&
          selectedIndex === baseIndex + results.length
        ) {
          // Create new player option
          handleCreatePlayer()
        } else if (selectedIndex === -1 && results.length > 0) {
          // No explicit selection with arrows: default to first result
          handleSelectPlayer(results[0])
        } else if (selectedIndex === -1 && results.length === 0 && searchTerm.trim()) {
          // No results at all — default to creating a new player
          handleCreatePlayer()
        }
        break
      case 'Escape':
        setIsOpen(false)
        setSelectedIndex(-1)
        break
      default:
        break
    }
  }

  // Clear selection
  const handleClear = (e) => {
    e.preventDefault()
    e.stopPropagation()
    onChange(null)
    setSearchTerm('')
    setHasInvalidInput(false)
    inputRef.current?.focus()
  }

  const baseIndex = !searchTerm.trim() ? 1 : 0
  const createIndex = baseIndex + results.length

  return (
    <div ref={wrapperRef} className="relative">
      {/* Display selected player or input */}
      {value ? (
        <div
          className="flex h-10 items-center gap-2 px-3 py-2 border rounded-md bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          tabIndex={tabIndex}
        >
          <User className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm">{value.name}</span>
          {value.gender && divisionGender === 'mixed' && (
            <span className="text-xs text-muted-foreground">
              ({value.gender})
            </span>
          )}
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => {
              // Force uppercase as user types
              setSearchTerm(e.target.value.toUpperCase())
              setIsOpen(true)
              setSelectedIndex(-1)
              setHasInvalidInput(false) // Clear error when typing
            }}
            onFocus={() => {
              setIsOpen(true)
              setHasInvalidInput(false) // Clear error on focus
            }}
            onBlur={() => {
              // Add delay to allow click events to register before closing
              blurTimeoutRef.current = setTimeout(() => {
                setIsOpen(false)
                // If there's text but no player selected, mark as invalid
                if (searchTerm.trim() && !value) {
                  setHasInvalidInput(true)
                }
              }, 200)
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            tabIndex={tabIndex}
            data-validation-key={validationKey || undefined}
            className={`w-full h-10 pl-9 pr-3 py-2 rounded-md text-sm focus-visible:outline-none uppercase ${
              error || hasInvalidInput 
                ? 'border-2 border-destructive bg-destructive/10 focus-visible:ring-2 focus-visible:ring-destructive' 
                : 'border border-input bg-background focus-visible:ring-2 focus-visible:ring-ring'
            }`}
            style={{ textTransform: 'uppercase' }}
          />
        </div>
      )}

      {/* Dropdown results */}
      {isOpen && !value && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
          {/* TBD Option - only when no search input */}
          {!searchTerm.trim() && (
            <>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSelectTBD}
                tabIndex={-1}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 cursor-pointer ${
                  selectedIndex === 0 ? 'bg-accent' : ''
                }`}
              >
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">TBD</span>
                <span className="text-xs text-muted-foreground ml-1">(To Be Determined)</span>
              </button>
              <div className="border-t" />
            </>
          )}

          {loading ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              Searching...
            </div>
          ) : results.length > 0 ? (
            <>
              {results.map((player, index) => (
                <button
                  key={player.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelectPlayer(player)}
                  tabIndex={-1}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center justify-between cursor-pointer ${
                    selectedIndex === baseIndex + index ? 'bg-accent' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>{player.name}</span>
                    {player.gender && divisionGender === 'mixed' && (
                      <span className="text-xs text-muted-foreground ml-1">
                        ({player.gender})
                      </span>
                    )}
                  </div>
                </button>
              ))}
              <div className="border-t" />
            </>
          ) : searchTerm.trim() ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No players found
            </div>
          ) : null}

          {/* Create new player option */}
          {searchTerm.trim() && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleCreatePlayer}
              disabled={creating}
              tabIndex={-1}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-center gap-2 border-t cursor-pointer ${
                selectedIndex === createIndex ? 'bg-accent' : ''
              }`}
            >
              <Plus className="h-4 w-4" />
              <span>
                {creating 
                  ? 'Creating...' 
                  : `Create "${searchTerm.trim()}"${newPlayerGender ? ` (${newPlayerGender})` : ''}`
                }
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  )
}
